import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Alert } from '../services/AlertsService';
import { Dashboard } from '../../dashboards/services/DashboardsService';

interface ActiveAlertsTableProps {
  alerts: Alert[];
  dashboards: Dashboard[];
  onEndAlert: (alertId: number) => void;
  loading: boolean;
}

const ActiveAlertsTable: React.FC<ActiveAlertsTableProps> = ({
  alerts,
  dashboards,
  onEndAlert,
  loading
}) => {
  // Helper function to get dashboard name by ID
  const getDashboardName = (id: number): string => {
    return dashboards.find(d => d.id === id)?.name || `Dashboard #${id}`;
  };

  // Helper function to render location
  const renderLocation = (alert: Alert) => {
    const locations: string[] = [];
    
    if (alert.site) locations.push('Site');
    if (alert.hub) locations.push('Hub');
    
    if (alert.dashboards && alert.dashboards.length > 0) {
      if (alert.dashboards.length === 1) {
        locations.push(getDashboardName(alert.dashboards[0]));
      } else {
        // Group dashboards by category for tooltip
        const dashboardsByCategory = alert.dashboards.reduce((acc, id) => {
          const dashboard = dashboards.find(d => d.id === id);
          if (dashboard) {
            const categoryName = dashboard.category?.name || 'Uncategorized';
            if (!acc[categoryName]) {
              acc[categoryName] = [];
            }
            acc[categoryName].push(dashboard.name);
          }
          return acc;
        }, {} as Record<string, string[]>);

        const tooltipContent = (
          <Box>
            {Object.entries(dashboardsByCategory).map(([category, names]) => (
              <Box key={category} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {category}
                </Typography>
                {names.map(name => (
                  <Typography key={name} variant="body2" sx={{ pl: 1 }}>
                    • {name}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        );

        return (
          <Box>
            {locations.join(', ')}
            {locations.length > 0 && ', '}
            <Tooltip title={tooltipContent} arrow>
              <span style={{ textDecoration: 'underline', cursor: 'help' }}>
                Dashboards ({alert.dashboards.length})
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
    
    return locations.join(', ') || 'None';
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (alerts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No active alerts
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Message</strong></TableCell>
            <TableCell><strong>Location</strong></TableCell>
            <TableCell><strong>Created</strong></TableCell>
            <TableCell align="center"><strong>Action</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alerts.map((alert) => (
            <TableRow key={alert.id}>
              <TableCell sx={{ whiteSpace: 'pre-line' }}>{alert.message}</TableCell>
              <TableCell>{renderLocation(alert)}</TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">{alert.createdBy.name}</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {formatDate(alert.createDate)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="center">
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => onEndAlert(alert.id)}
                >
                  End Alert
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ActiveAlertsTable;
